import React, { useState } from 'react';
import { FaRuler, FaWeight, FaThermometerHalf } from 'react-icons/fa';
import { IconType } from 'react-icons';
// @ts-ignore
import { Helmet } from 'react-helmet';

type ConversionType = 'length' | 'weight' | 'temperature';

interface ConversionUnit {
  value: string;
  label: string;
  factor?: number;
}

interface ConversionTypeConfig {
  label: string;
  units: ConversionUnit[];
  icon: IconType;
}

const conversionTypes: { [key in ConversionType]: ConversionTypeConfig } = {
  length: {
    label: 'Length',
    icon: FaRuler,
    units: [
      { value: 'mm', label: 'Millimeters', factor: 0.001 },
      { value: 'cm', label: 'Centimeters', factor: 0.01 },
      { value: 'm', label: 'Meters', factor: 1 },
      { value: 'km', label: 'Kilometers', factor: 1000 },
      { value: 'in', label: 'Inches', factor: 0.0254 },
      { value: 'ft', label: 'Feet', factor: 0.3048 },
      { value: 'yd', label: 'Yards', factor: 0.9144 },
      { value: 'mi', label: 'Miles', factor: 1609.344 },
    ],
  },
  weight: {
    label: 'Weight',
    icon: FaWeight,
    units: [
      { value: 'mg', label: 'Milligrams', factor: 0.001 },
      { value: 'g', label: 'Grams', factor: 1 },
      { value: 'kg', label: 'Kilograms', factor: 1000 },
      { value: 't', label: 'Metric Tons', factor: 1000000 },
      { value: 'oz', label: 'Ounces', factor: 28.3495 },
      { value: 'lb', label: 'Pounds', factor: 453.592 },
      { value: 'st', label: 'Stones', factor: 6350.29 },
    ],
  },
  temperature: {
    label: 'Temperature',
    icon: FaThermometerHalf,
    units: [
      { value: 'c', label: 'Celsius' },
      { value: 'f', label: 'Fahrenheit' },
      { value: 'k', label: 'Kelvin' },
    ],
  },
};

const Converter: React.FC = () => {
  const [conversionType, setConversionType] = useState<ConversionType>('length');
  const [fromUnit, setFromUnit] = useState<string>('');
  const [toUnit, setToUnit] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [result, setResult] = useState<string>('');

  const convertValue = () => {
    if (!inputValue || !fromUnit || !toUnit) return;

    const value = parseFloat(inputValue);
    if (isNaN(value)) return;

    let convertedValue: string | number;

    if (conversionType === 'temperature') {
      // Special handling for temperature conversions
      convertedValue = convertTemperature(value, fromUnit, toUnit);
    } else {
      // Standard unit conversion using factors
      const fromFactor = conversionTypes[conversionType].units.find(u => u.value === fromUnit)?.factor || 1;
      const toFactor = conversionTypes[conversionType].units.find(u => u.value === toUnit)?.factor || 1;
      convertedValue = (value * fromFactor) / toFactor;
    }

    setResult(typeof convertedValue === 'number' ? convertedValue.toFixed(6) : convertedValue);
  };

  const convertTemperature = (value: number, from: string, to: string): number => {
    // Convert to Celsius first
    let celsius: number;
    switch (from) {
      case 'c':
        celsius = value;
        break;
      case 'f':
        celsius = (value - 32) * 5/9;
        break;
      case 'k':
        celsius = value - 273.15;
        break;
      default:
        celsius = value;
    }

    // Convert from Celsius to target unit
    switch (to) {
      case 'c':
        return celsius;
      case 'f':
        return (celsius * 9/5) + 32;
      case 'k':
        return celsius + 273.15;
      default:
        return celsius;
    }
  };

  const renderIcon = (type: ConversionType) => {
    const IconComponent = conversionTypes[type].icon;
    // @ts-ignore
    return <IconComponent size={20} />;
  };

  return (
    <>
      <Helmet>
        <title>Unit Converter | Convert Units Online - Davinqi</title>
        <meta name="description" content="Convert between different units of measurement instantly with Davinqi Unit Converter. Accurate, fast, and privacy-friendly." />
        <meta property="og:title" content="Unit Converter | Convert Units Online - Davinqi" />
        <meta property="og:description" content="Convert between different units of measurement instantly with Davinqi Unit Converter. Accurate, fast, and privacy-friendly." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://davinqi.com/math/converter" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Unit Converter | Convert Units Online - Davinqi" />
        <meta name="twitter:description" content="Convert between different units of measurement instantly with Davinqi Unit Converter. Accurate, fast, and privacy-friendly." />
      </Helmet>
      <div className="relative">
        <div className="max-w-4xl mx-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden p-6">
          <div className="space-y-6">
            {/* Conversion Type Selection */}
            <div className="grid grid-cols-3 gap-4">
              {(Object.keys(conversionTypes) as ConversionType[]).map((type) => (
                <button
                  key={type}
                  className={`p-3 text-sm transition-colors border border-gray-200 dark:border-gray-600 rounded-lg flex items-center justify-center gap-2 ${
                    conversionType === type
                      ? 'bg-gray-900 dark:bg-gray-600 text-white'
                      : 'bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-gray-600'
                  }`}
                  onClick={() => setConversionType(type)}
                >
                  {renderIcon(type)}
                  {conversionTypes[type].label}
                </button>
              ))}
            </div>

            {/* Conversion Inputs */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  From
                </label>
                <select
                  value={fromUnit}
                  onChange={(e) => setFromUnit(e.target.value)}
                  className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select unit</option>
                  {conversionTypes[conversionType].units.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Value
                </label>
                <input
                  type="number"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter value"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  To
                </label>
                <select
                  value={toUnit}
                  onChange={(e) => setToUnit(e.target.value)}
                  className="w-full p-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select unit</option>
                  {conversionTypes[conversionType].units.map((unit) => (
                    <option key={unit.value} value={unit.value}>
                      {unit.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Convert Button */}
            <button
              onClick={convertValue}
              className="w-full bg-gray-900 dark:bg-gray-600 text-white p-3 text-sm hover:bg-gray-800 dark:hover:bg-gray-500 transition-colors rounded-lg"
            >
              Convert
            </button>

            {/* Result Display */}
            {result && (
              <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Result</h3>
                <p className="text-gray-700 dark:text-gray-300">
                  {`${inputValue} ${fromUnit} = ${result} ${toUnit}`}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Converter; 